import React, { Dispatch, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../styles'
import Button from '../button/Button'
import { useMediaQuery } from 'react-responsive'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import fetchFactory from '../../utils/fetchFactory'
import { connect, useDispatch, useSelector } from 'react-redux'
import errorCatcher from '../../utils/errorCatcher'
import { FormattedMessage, useIntl } from 'react-intl'
import { AlertModel } from '../../types/alert/alertModel'
import { saveAlerts } from '../../modules/reducers/alert/actions'
import { useHistory, useLocation } from 'react-router'
import links from '../../enums/linksEnum'
import { RootState } from '../../modules/reducers'
import Icon from '../icon/Icon'
import { ButtonBase } from '@material-ui/core'
import iconsEnum from '../../enums/iconsEnum'
import PrelevementAlert from '../customAlert/PrelevementAlert'
import { ContractsModel } from '../../types/contracts/contractsModel'
import { PrelevementModalModel } from '../../types/prelevement/prelevementModalModel'
import { savePrelevement } from '../../modules/reducers/prelevement/actions'
import { getLatestDateRelancePrelevement, setDateRelance } from '../../modules/contracts/contractMapper'
import { RequestItemModel } from '../../modules/requests/requestsModel'
import { canDisplayCustomAlert } from './utils/debitUtils'
import { AssuranceModalModel } from '../../types/assurance/assuranceModalModel'
import AssuranceAlert from '../customAlert/AssuranceAlert'
import { saveAssurance } from '../../modules/reducers/assurance/actions'

const useStyle = makeStyles((theme) => (
	{
		...styles(theme),
		topAlertStyle: {
			background: theme.palette.accessibility.main,
			width: '100%',
			color: 'white',
			marginBottom: 25,

			'& .container': {
				position: 'relative',
				display: 'flex',
				alignItems: 'center',
				textAlign: 'left',
				width: '80%',
				maxWidth: 1100,
				margin: 'auto',
				padding: '25px 0',

				'& div:first-child': {
					width: '65%',
					marginRight: 60,

					'& p:first-child': {
						marginTop: 0
					},
					'& p:last-child': {
						marginBottom: 0
					}
				},
				'& div:nth-child(2)': {},
				'& img': {
					position: 'absolute',
					top: 15,
					right: 0,
					width: 24,
					background: '#4cc4c2',
					padding: 7,
					borderRadius: '100%',
					boxSizing: 'border-box',
					cursor: 'pointer'
				}
			}
		},
		topAlertLargeScreenStyle: {
			paddingTop: 100
		},
		topAlertSmallScreenStyle: {
			paddingTop: 65,
			'& .container': {
				'& div:first-child': {
					marginRight: 10
				}
			}
		},
		circularProgressStyle: {
			marginBottom: 20
		},
		circularProgressLargeScreenStyle: {
			paddingTop: 100
		},
		circularProgressSmallScreenStyle: {
			paddingTop: 65
		},
		closeButton: {
			width: 24,
			height: 24,
			borderRadius: 40,
			position: 'absolute',
			top: 15,
			right: 0,
			background: theme.palette.green['70'],
			padding: 7,
			'& svg': {
				fill: theme.palette.textAccessibility.main
			}
		}
	}
))

interface AcceptingProps {
	isTopAlertOpen: boolean,
	onTopAlertClosed: () => void
}

interface Store {
	errorCatcher: (error: any, id: string) => void,
	contracts: ContractsModel[],
	prelevement: PrelevementModalModel,
	assurance: AssuranceModalModel,
	requests: RequestItemModel[],
	savePrelevement: (prelevementData: PrelevementModalModel) => void,
}

type TopAlertProps =
	AcceptingProps
	& Store

const COMPONENT_ID = 'topAlert'

const TopAlert: React.FC<TopAlertProps> = (
	{
		isTopAlertOpen,
		onTopAlertClosed,
		errorCatcher,
		contracts,
		prelevement,
		assurance,
		savePrelevement,
		requests
	}
) => {
	const classes = useStyle()
	const history = useHistory()
	const intl = useIntl()
	const oldLanguage = useRef(intl.locale)
	let alerts = useSelector((state: RootState) => state.alert)
	const dispatch = useDispatch()
	const [alert, setAlert] = useState<AlertModel>(
		{
			id: '',
			beginning: '',
			title: '',
			information: '',
			description: ''
		}
	)
	const [loading, setLoading] = useState<boolean>(true)

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const topAlertClass: ClassValue = classNames(
		classes.topAlertStyle,
		{
			[classes.topAlertLargeScreenStyle]: isLargeScreen,
			[classes.topAlertSmallScreenStyle]: !isLargeScreen
		}
	)

	const circularProgressClass: ClassValue = classNames(
		classes.circularProgressStyle,
		isLargeScreen ? classes.circularProgressLargeScreenStyle : classes.circularProgressSmallScreenStyle
	)

	useEffect(
		() => {
			if (alerts.length === 0 || oldLanguage.current !== intl.locale) {
				setLoading(true)

				fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/information/getTopAlert?language=${intl.locale}`)
					.then(
						(alertsResponse: any) => {
							if (alertsResponse.length > 0) {
								dispatch(
									saveAlerts(alertsResponse)
								)
								setAlert(alertsResponse[0])
							} else {
								onTopAlertClosed()
							}
						}
					)
					.catch(
						(err) => {
							errorCatcher(err, COMPONENT_ID)
							onTopAlertClosed()
						}
					)
					.finally(
						() => {
							setLoading(false)
							oldLanguage.current = intl.locale
						}
					)
			}
		}, [alerts.length, dispatch, errorCatcher, intl.locale, onTopAlertClosed]
	)

	const alertAction = () => {
		history.push(`${links.alertDetails}/${alert.id}`)
	}

	const nextAlert = () => {
		dispatch(
			saveAlerts(alerts.splice(0, 1))
		)

		if (alerts.length > 0) {
			setAlert(alerts[0])
		} else {
			onTopAlertClosed()
		}
	}

	const closeButtonClass: ClassValue = classNames(
		classes.closeButton
	)
	const location = useLocation()

	const closeCustomAlert = () => {
		savePrelevement({
			subscribePrelevementModal: false
		})
		setDateRelance(getLatestDateRelancePrelevement(contracts).interlocutor)
	}

	return (
		<>
			{
				(assurance.finAssuranceProche || assurance.assuranceFinie) ?
					<AssuranceAlert />
					:
					prelevement.subscribePrelevementModal && canDisplayCustomAlert(contracts, requests) && location.pathname === links.contracts ?
						<PrelevementAlert
							onCustomAlertClosed={closeCustomAlert}
						/>
						:
						isTopAlertOpen && !loading &&
						(
							<div className={topAlertClass}>
								<div className="container">
									<div>
										<p className={classes.text4}>{alert.title}</p>

										<p className={classes.text1}>{alert.information}</p>
									</div>

									{
										alert?.description &&
										<div>
											<Button
												onClick={alertAction}
												color="colorWhite"
												textStyle="text2"
												disabled={false}
												background="backgroundInherit"
												border="borderWhite"
												ariaLabel={
													intl.formatMessage(
														{
															id: 'topAlert.ariaLabel',
															defaultMessage: 'En savoir plus sur l\'alerte : {alert}',
															description: 'Aria label text'
														},
														{ alert: alert.title }
													)
												}
											>
												<FormattedMessage
													id="app.details"
													defaultMessage="En savoir plus"
													description="Button text"
												/>
											</Button>
										</div>
									}
									<ButtonBase className={closeButtonClass} onClick={nextAlert}>
										<Icon icon={iconsEnum.Cross} size={11} />
									</ButtonBase>
								</div>
							</div>
						)
			}
			{
				isTopAlertOpen && loading &&
				<div className={circularProgressClass}>
				</div>
			}
		</>
	)
}

const mapStateToProps = (state: RootState) => (
	{
		contracts: state.contracts,
		prelevement: state.prelevement,
		assurance: state.assurance,
		requests: state.requests
	}
)

const mapDispatchToProps = (dispatch: Dispatch<any>) => (
	{
		errorCatcher: (error: any, id: string) => {
			dispatch(errorCatcher(error, id))
		},
		savePrelevement: (prelevementData: PrelevementModalModel) => {
			dispatch(savePrelevement(prelevementData))
		},
		saveAssurance: (assuranceData: AssuranceModalModel) => {
			dispatch(saveAssurance(assuranceData))
		}
	}
)

export default connect(mapStateToProps, mapDispatchToProps)(TopAlert)
